// Intel — surfaces the Autonomous Brain's daily digest.
//
// Layout (rewritten 2026-05-26):
//   Header (H1 + date dropdown + "last ran" subtitle)
//     ↓
//   PageStatsRow (opportunities count / min/wk saved / tokens used)
//     ↓
//   SummaryCard (prose summary of the selected digest)
//     ↓
//   Automation opportunities (most actionable — first)
//     ↓
//   Where your time went (time_breakdown bars)
//     ↓
//   Top relationships (with sort toggle)
//     ↓
//   Pattern observations
//     ↓
//   <details>Previous digests</details>  (history disclosure — demoted)
//
// Why this shape: the Brain ticks ~daily, so 95% of the time the reader
// only cares about the most recent digest. Previous layout buried it
// behind a left-pane click-to-reveal — confusing for a one-current+
// archive content shape. Inspired by the prototype's single-scroll feed.
//
// Read shape:
//   - autonomous_brain_log: one row per UTC day, contains summary +
//     window_end. Used to populate the date dropdown + the prose summary
//     for the selected day.
//   - digest_entries: per-pattern rows grouped by kind. Owner-readable
//     payload JSON, rendered via kind-specific visualizations (bars,
//     relationship cards, opportunity cards, observation cards).
//
// Mutation shape: UPDATE digest_entries (dismiss / undismiss).
//   - Dismiss sets dismissed=true + dismissed_at=now(); the CHECK
//     constraint digest_entries_dismissed_consistency requires both to
//     move together.
//   - Undo (from the sonner toast action) re-flips both to false / null.
//   - Optimistic cache update: row vanishes from the kind-grouped list
//     immediately; toast surfaces an undo handle for ~5s.
//   - audit_log gets the row automatically via add_audit_trigger
//     (migration 0023, line 289). No N4 daemon contract impact — this
//     is an owner-initiated UI write, not a memoryWrites declaration.
//
// Polling: React Query refetches every 60s — the Brain ticks once per
// UTC day so updates are rare. The asynchronous daemon may land today's
// digest after page load; one minute is the right granularity.
//
// Screen field audit (per CLAUDE.md §19 standard review): every visible
// field traces to its writer:
//   - brain_log.summary           ← worker insertBrainLog(toolUseResult.summary)
//   - brain_log.window_start/end  ← worker insertBrainLog(window dates)
//   - brain_log.input/output_token_count ← worker insertBrainLog(tokens)
//   - digest_entries.kind         ← worker explodeDigestEntries (4 enum values)
//   - digest_entries.payload      ← worker explodeDigestEntries (entry verbatim
//                                    from EMIT_DIGEST_TOOL.input_schema in
//                                    apps/workers/src/agents/brain/prompts/brain.ts)
//   - digest_entries.dismissed    ← user click on the row's X button
//                                    (this file, dismissEntry/undoDismiss
//                                    mutations; buildDismissUpdate /
//                                    buildUndoUpdate compose the body)
//   - digest_entries.dismissed_at ← same path, set atomically with dismissed
//   - digest_entries.digest_date  ← worker utcDateString(windowEnd)
//
// Every payload field rendered below is enumerated in EMIT_DIGEST_TOOL:
//   time_breakdown:         bucket, percent, example_refs?
//   top_relationship:       counterparty, touchpoint_count, last_touched_at,
//                           channels?, note?
//   automation_opportunity: opportunity, evidence, est_time_saved_per_week_minutes?,
//                           data_points?, data_points_target?, confidence?,
//                           agent_type? ('agent'|'skill'), status? — the last five
//                           drive the Automation Queue card (AutomationCard) and are
//                           now emitted by the Brain (EMIT_DIGEST_TOOL in
//                           apps/workers/src/agents/brain/prompts/brain.ts); the
//                           card also renders when they're absent (safe defaults).
//
//   pattern_observation:    observation, confidence, grounded_in?
//
// Regenerate: the header "Regenerate" button (owner JWT) first SOFT-DELETES
// today's brain log + entries — the daemon/service_role is blocked from that by
// the migration-0026 column guard, so the owner frees the day-index — then
// upserts an owner-writable app_settings row 'brain_regenerate_request:<owner>'
// (migration 0050). The Brain daemon polls it, runs a normal tick (today's log
// now absent → re-synthesises + inserts fresh), and DELETEs the row as its ack.
// The page watches for that clear, refetches, and reports honestly (a today log
// present = success; absent = the Brain didn't produce one).
//   (Skills Learned reads the agent_learnings table: agent, title, source, learned_at.)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import {
  Brain,
  Sparkles,
  Users,
  Wrench,
  Clock,
  Cpu,
  ChevronDown,
  ChevronRight,
  X,
  Bot,
  Play,
  Pause,
  Pencil,
  Trash2,
  Zap,
  GraduationCap,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import PageStatsRow, { type PageStat } from '@/components/shared/PageStatsRow';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import EditAutomationModal from '@/components/intel/EditAutomationModal';

export type DigestEntryKind =
  | 'time_breakdown'
  | 'top_relationship'
  | 'automation_opportunity'
  | 'pattern_observation';

export type DigestEntryRow = {
  id: string;
  digest_date: string;
  kind: DigestEntryKind;
  payload: Record<string, unknown>;
  source_brain_log_id: string;
  dismissed: boolean;
  created_at: string;
};

export type BrainLogRow = {
  id: string;
  summary: string;
  window_start: string;
  window_end: string;
  input_token_count: number;
  output_token_count: number;
  created_at: string;
};

async function fetchRecentBrainLogs(): Promise<BrainLogRow[]> {
  // .limit(7) is intentional and matches the Brain's 7-day reading window
  // (one log per day → at most 7 unique digests of interest). Don't paginate;
  // older digests are read-only audit trail accessed via SQL, not the UI.
  const { data, error } = await supabase
    .from('autonomous_brain_log')
    .select(
      'id, summary, window_start, window_end, input_token_count, output_token_count, created_at',
    )
    .is('deleted_at', null)
    .order('window_end', { ascending: false })
    .limit(7);
  if (error) throw error;
  return (data as unknown as BrainLogRow[]) ?? [];
}

export async function fetchEntriesForLog(brainLogId: string | null): Promise<DigestEntryRow[]> {
  if (!brainLogId) return [];
  const { data, error } = await supabase
    .from('digest_entries')
    .select('id, digest_date, kind, payload, source_brain_log_id, dismissed, created_at')
    .eq('source_brain_log_id', brainLogId)
    .eq('dismissed', false)
    .is('deleted_at', null)
    .order('kind', { ascending: true });
  if (error) throw error;
  return (data as unknown as DigestEntryRow[]) ?? [];
}

// CHECK constraint digest_entries_dismissed_consistency (migration 0023
// line 215) requires dismissed + dismissed_at to be either both null/false
// or both set. These two helpers are the single source of truth for the
// update body so a future refactor can't drop one half — unit-tested in
// Intel.test.tsx under "dismiss update body".
export function buildDismissUpdate(now: Date): { dismissed: true; dismissed_at: string } {
  return { dismissed: true, dismissed_at: now.toISOString() };
}

export function buildUndoUpdate(): { dismissed: false; dismissed_at: null } {
  return { dismissed: false, dismissed_at: null };
}

// Is there a (non-deleted) brain log whose window_end falls on the current UTC
// day? Drives the "today's digest is in" indicator AND the Regenerate
// completion check (after a regen, a today log present = it worked; absent = the
// Brain didn't produce one, e.g. paused). Pure + exported for tests.
export function hasLogForUtcDay(
  logs: Array<{ window_end: string }>,
  now: Date = new Date(),
): boolean {
  const start = new Date(now);
  start.setUTCHours(0, 0, 0, 0);
  return logs.some((l) => new Date(l.window_end) >= start);
}

// ===========================================================================
// Automation Queue — persistent, owner-owned. Reads from the `automations`
// table (migration 0051), NOT digest_entries. Rows survive Brain regen,
// log soft-deletes, and Sonnet's day-to-day drift. Owner has full control:
// Keep, Defer, Edit, Delete, Activate. See task #39.
// ===========================================================================

export type AutomationRow = {
  id: string;
  opportunity_key: string;
  opportunity: string;
  evidence: string | null;
  est_time_saved_per_week_minutes: number | null;
  data_points: number | null;
  data_points_target: number | null;
  confidence: number | null;
  agent_type: 'agent' | 'skill' | null;
  status: 'gathering' | 'ready' | null;
  user_status: 'open' | 'deferred' | 'activated' | 'deleted';
  activated_at: string | null;
  deferred_at: string | null;
  ticket_id: string | null;
  edit_history: Array<{
    edited_at: string;
    field: string;
    before: unknown;
    after: unknown;
    edited_by: string;
  }>;
  created_at: string;
  updated_at: string;
};

const AUTOMATION_SELECT =
  'id, opportunity_key, opportunity, evidence, est_time_saved_per_week_minutes, data_points, data_points_target, confidence, agent_type, status, user_status, activated_at, deferred_at, ticket_id, edit_history, created_at, updated_at';

async function fetchAutomations(): Promise<AutomationRow[]> {
  // Owner-scoped read via RLS. Include activated so we can render the
  // "✓ Activated" pill; deferred surfaces under a collapsed disclosure;
  // deleted never renders (blocks Brain resurrection at write time).
  const { data, error } = await supabase
    .from('automations')
    .select(AUTOMATION_SELECT)
    .is('deleted_at', null)
    .in('user_status', ['open', 'activated', 'deferred'])
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as unknown as AutomationRow[]) ?? [];
}

const KIND_META: Record<DigestEntryKind, { label: string; icon: typeof Clock; tone: string }> = {
  time_breakdown: { label: 'Where your time went', icon: Clock, tone: 'text-forest' },
  top_relationship: { label: 'Top relationships', icon: Users, tone: 'text-sage' },
  automation_opportunity: {
    label: 'Automation opportunities',
    icon: Wrench,
    tone: 'text-attention',
  },
  pattern_observation: { label: 'Pattern observations', icon: Sparkles, tone: 'text-learning' },
};

// Render order: actionable first (opportunities), then the descriptive
// time/relationship blocks, then the lowest-priority "skill-learned"
// pattern observations. Mirrors the prototype's "Insights Feed →
// Automation Queue → Skills Learned" rhythm.
const KIND_ORDER: DigestEntryKind[] = [
  'automation_opportunity',
  'time_breakdown',
  'top_relationship',
  'pattern_observation',
];

const SHORT_SUMMARY_THRESHOLD = 80;

const Intel = () => {
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [relationshipSort, setRelationshipSort] = useState<'touchpoints' | 'recency'>(
    'touchpoints',
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const qc = useQueryClient();
  const { user } = useAuth();
  const [regenPending, setRegenPending] = useState(false);

  const logsQ = useQuery({
    queryKey: ['brain_logs'],
    queryFn: fetchRecentBrainLogs,
    refetchInterval: 60_000,
  });

  const effectiveLogId = useMemo(() => {
    if (selectedLogId) return selectedLogId;
    return logsQ.data?.[0]?.id ?? null;
  }, [selectedLogId, logsQ.data]);

  const entriesQ = useQuery({
    queryKey: ['digest_entries', effectiveLogId],
    queryFn: () => fetchEntriesForLog(effectiveLogId),
    refetchInterval: 60_000,
    enabled: !!effectiveLogId,
  });

  // Optimistic dismiss with sonner undo.
  //
  // Cache key is derived from row.source_brain_log_id (NOT effectiveLogId)
  // so undo lands on the right cache even if the user switches the date
  // dropdown between dismiss and Undo. (R1 BLOCKER fix.)
  //
  // onError invalidates instead of restoring a stale snapshot — under
  // rapid concurrent dismissals, a blind snapshot restore could re-add
  // rows that were successfully dismissed by a sibling mutation. Letting
  // the server be source-of-truth on failure costs one refetch and one
  // brief gap; correctness wins. (R1 STRONG NIT S1 fix.)
  const dismissEntry = useMutation({
    mutationFn: async (args: { row: DigestEntryRow }) => {
      const { error } = await supabase
        .from('digest_entries')
        .update(buildDismissUpdate(new Date()))
        .eq('id', args.row.id);
      if (error) throw error;
    },
    onMutate: async ({ row }) => {
      const key = ['digest_entries', row.source_brain_log_id] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DigestEntryRow[]>(key) ?? [];
      qc.setQueryData<DigestEntryRow[]>(
        key,
        prev.filter((r) => r.id !== row.id),
      );
      return { key };
    },
    onError: (_err, _args, ctx) => {
      if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
      toast.error('Failed to dismiss — entry restored');
    },
    onSuccess: (_data, { row }) => {
      toast('Dismissed', {
        description: 'Reappears only if the Brain re-surfaces it.',
        action: {
          label: 'Undo',
          onClick: () => undoDismiss.mutate({ row }),
        },
      });
    },
  });

  const undoDismiss = useMutation({
    mutationFn: async ({ row }: { row: DigestEntryRow }) => {
      const { error } = await supabase
        .from('digest_entries')
        .update(buildUndoUpdate())
        .eq('id', row.id);
      if (error) throw error;
    },
    onMutate: async ({ row }) => {
      // Optimistic restore — symmetric with dismissEntry's instant feedback.
      // Re-prepend; KindCard's sort logic re-orders for display.
      const key = ['digest_entries', row.source_brain_log_id] as const;
      await qc.cancelQueries({ queryKey: key });
      const current = qc.getQueryData<DigestEntryRow[]>(key) ?? [];
      qc.setQueryData<DigestEntryRow[]>(key, [row, ...current]);
      return { key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
      toast.error('Failed to restore — refresh to retry');
    },
  });

  // Persistent Automation Queue — reads the owner-owned `automations` table
  // (migration 0051). Rows survive Brain regen + log soft-deletes; owner has
  // full control (Keep / Defer / Edit / Delete / Activate). The 4→2→1 melt is
  // gone: the queue is decoupled from `effectiveLogId`.
  const automationsQ = useQuery({
    queryKey: ['automations', user?.id ?? null],
    queryFn: fetchAutomations,
    refetchInterval: 60_000,
    enabled: !!user,
  });

  // Edit modal is owned here (single row in flight at a time). null = closed.
  const [editingRow, setEditingRow] = useState<AutomationRow | null>(null);

  // Defer / Keep (un-defer) / Delete / Activate — all owner-JWT UPDATEs on
  // automations. RLS is is_owner(owner_id); the 0051 CHECK constraints keep
  // user_status + activated_at coherent.
  const setAutomationStatus = useMutation({
    mutationFn: async (args: {
      row: AutomationRow;
      user_status: 'open' | 'deferred' | 'deleted';
    }) => {
      const nowIso = new Date().toISOString();
      const patch: Record<string, unknown> = { user_status: args.user_status };
      if (args.user_status === 'deferred') patch.deferred_at = nowIso;
      if (args.user_status === 'open') {
        // Undo path — a "back on the queue" click can follow EITHER Defer or
        // Delete. Clear BOTH stamps so the row rejoins the visible query
        // (fetchAutomations filters `.is('deleted_at', null)`); leaving
        // deleted_at set would silently orphan the row despite the toast
        // saying "Back on the queue".
        patch.deferred_at = null;
        patch.deleted_at = null;
      }
      if (args.user_status === 'deleted') patch.deleted_at = nowIso;
      const { error } = await supabase.from('automations').update(patch).eq('id', args.row.id);
      if (error) throw error;
    },
    onSuccess: (_data, { user_status, row }) => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      if (user_status === 'deferred')
        toast('Deferred', {
          description: 'Hidden from the main queue — Keep to bring it back.',
          action: {
            label: 'Undo',
            onClick: () => setAutomationStatus.mutate({ row, user_status: 'open' }),
          },
        });
      else if (user_status === 'deleted')
        toast('Deleted', {
          description: 'Won’t reappear in future digests.',
          action: {
            label: 'Undo',
            onClick: () => setAutomationStatus.mutate({ row, user_status: 'open' }),
          },
        });
      else toast.success('Back on the queue.');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not update automation.'),
  });

  // Activate = file a build ticket AND mark the row activated (durable across
  // refresh, unlike the old in-memory activatedIds Set). Order matters: we
  // UPDATE automations FIRST (activated_at set, ticket_id still null — the
  // CHECK constraint activations_activated_has_ts only requires activated_at,
  // not ticket_id) so a mid-run failure cannot leave a ticket floating without
  // its automation. Then INSERT the ticket + patch ticket_id. Client
  // idempotency guard: skip if the row is already activated (belt-and-braces
  // against a stale click firing after a successful mutation).
  const activateAutomation = useMutation({
    mutationFn: async (row: AutomationRow) => {
      if (row.user_status === 'activated') {
        // Idempotency: another tab or a rapid double-click already activated
        // this row. Refetch and return — don't file a duplicate ticket.
        return;
      }
      const confidence = row.confidence ?? 0;
      const { error: updErr } = await supabase
        .from('automations')
        .update({
          user_status: 'activated',
          activated_at: new Date().toISOString(),
        })
        .eq('id', row.id)
        .eq('user_status', 'open'); // second-line idempotency: only flip an open row
      if (updErr) throw updErr;
      const { data: ticket, error: ticketErr } = await supabase
        .from('tickets')
        .insert({
          title: `Activate automation: ${row.opportunity}`.slice(0, 200),
          description: `Requested from the Intel Automation Queue.\n\n${row.evidence ?? ''}`.trim(),
          feature_area: 'Intel / Overview',
          kind: 'feature_request',
          severity: confidence >= 85 ? 'high' : 'medium',
        })
        .select('id')
        .single();
      if (ticketErr) throw ticketErr;
      const { error: patchErr } = await supabase
        .from('automations')
        .update({ ticket_id: (ticket as { id: string } | null)?.id ?? null })
        .eq('id', row.id);
      if (patchErr) throw patchErr;
    },
    onSuccess: () => {
      toast.success('Queued for build — added to Tickets.');
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['automations'] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not activate this automation.'),
  });

  // Which automation rows have a mutation in flight (any of set/activate/edit).
  // Buttons on those cards disable so a double-click can't fire the same
  // mutation twice. React Query's isPending is exposed via .variables, so we
  // read it directly without keeping a separate state.
  const pendingAutomationIds = useMemo(() => {
    const s = new Set<string>();
    if (setAutomationStatus.isPending && setAutomationStatus.variables?.row.id) {
      s.add(setAutomationStatus.variables.row.id);
    }
    if (activateAutomation.isPending && activateAutomation.variables?.id) {
      s.add(activateAutomation.variables.id);
    }
    return s;
  }, [
    setAutomationStatus.isPending,
    setAutomationStatus.variables,
    activateAutomation.isPending,
    activateAutomation.variables,
  ]);

  // Regenerate — force the Autonomous Brain to re-synthesise today's digest on
  // demand instead of waiting for the daily tick. The owner soft-deletes today's
  // digest (freeing the day-index — see the regenerate mutation) then writes the
  // request row (migration 0050); the Brain daemon polls it, re-runs, and clears
  // the row as its ack. We watch for that clear to refresh + report honestly.
  const regenRequestQ = useQuery({
    queryKey: ['brain_regen_request', user?.id ?? null],
    queryFn: async (): Promise<{ present: boolean }> => {
      if (!user) return { present: false };
      const { data } = await supabase
        .from('app_settings')
        .select('key')
        .eq('key', `brain_regenerate_request:${user.id}`)
        .maybeSingle();
      return { present: !!data };
    },
    enabled: regenPending && !!user,
    refetchInterval: regenPending ? 4000 : false,
  });

  // Completion: the daemon DELETEs the request row when it's done (or can't
  // run). When we observe that clear, refetch and report HONESTLY — a today log
  // present means it worked; absent means the Brain didn't produce one (paused /
  // budget / empty), so we don't falsely claim success.
  useEffect(() => {
    if (!regenPending || regenRequestQ.data?.present !== false) return;
    setRegenPending(false);
    setSelectedLogId(null); // jump to the freshest log once it lands
    void (async () => {
      await qc.invalidateQueries({ queryKey: ['brain_logs'] });
      await qc.invalidateQueries({ queryKey: ['digest_entries'] });
      const logs = qc.getQueryData<BrainLogRow[]>(['brain_logs']) ?? [];
      if (hasLogForUtcDay(logs)) toast.success('Digest regenerated.');
      else toast('No new digest produced — the Brain may be paused. Check the Agents page.');
    })();
  }, [regenPending, regenRequestQ.data, qc]);

  // Safety net: never leave the button spinning forever if the daemon is down
  // and never consumes the request. 2 minutes is well past a normal ~30s run.
  useEffect(() => {
    if (!regenPending) return;
    const t = window.setTimeout(() => {
      setRegenPending(false);
      qc.invalidateQueries({ queryKey: ['brain_logs'] });
      qc.invalidateQueries({ queryKey: ['digest_entries'] });
      toast('Regeneration is taking longer than expected — refresh to check.');
    }, 120_000);
    return () => window.clearTimeout(t);
  }, [regenPending, qc]);

  const regenerate = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user');
      const startOfUtcDay = new Date();
      startOfUtcDay.setUTCHours(0, 0, 0, 0);
      const nowIso = new Date().toISOString();
      // The daemon (service_role) is blocked by the migration-0026 guard from
      // modifying autonomous_brain_log.deleted_at, so the OWNER frees today's
      // slot here (owner RLS + guard allow it). Soft-delete today's log(s) and
      // their entries; the daemon then inserts a fresh digest for today.
      const { data: killed, error: logErr } = await supabase
        .from('autonomous_brain_log')
        .update({ deleted_at: nowIso })
        .eq('owner_id', user.id)
        .gte('window_end', startOfUtcDay.toISOString())
        .is('deleted_at', null)
        .select('id');
      if (logErr) throw logErr;
      const ids = ((killed ?? []) as Array<{ id: string }>).map((r) => r.id);
      if (ids.length > 0) {
        const { error: entErr } = await supabase
          .from('digest_entries')
          .update({ deleted_at: nowIso })
          .in('source_brain_log_id', ids)
          .is('deleted_at', null);
        if (entErr) throw entErr;
      }
      const payload = JSON.stringify({ requested_at: nowIso, requested_by: user.id });
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          { key: `brain_regenerate_request:${user.id}`, value: payload },
          { onConflict: 'key' },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      // Prime the poll cache to "present" so the completion effect waits for a
      // REAL clear. Without this, a second click reads the prior run's stale
      // {present:false} and resolves instantly.
      qc.setQueryData(['brain_regen_request', user?.id ?? null], { present: true });
      setRegenPending(true);
      toast('Regenerating digest…', {
        description: 'The Brain is re-analysing your recent week — about 30 seconds.',
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not start regeneration.'),
  });

  const selectedLog = logsQ.data?.find((l) => l.id === effectiveLogId) ?? null;

  const entriesByKind = useMemo(() => {
    const groups: Record<DigestEntryKind, DigestEntryRow[]> = {
      time_breakdown: [],
      top_relationship: [],
      automation_opportunity: [],
      pattern_observation: [],
    };
    for (const e of entriesQ.data ?? []) {
      groups[e.kind].push(e);
    }
    return groups;
  }, [entriesQ.data]);

  const todayHasLog = useMemo(() => hasLogForUtcDay(logsQ.data ?? []), [logsQ.data]);

  const mostRecent = logsQ.data?.[0] ?? null;

  // Stats strip — computed against the SELECTED digest, not the lifetime
  // total. "from this digest" so the reader doesn't conflate one-day
  // numbers with cumulative ones.
  const stats: PageStat[] = useMemo(() => {
    // Persistent Automation Queue counts — the queue is owner-scoped, not
    // digest-scoped, so these numbers reflect the full open queue rather than
    // just the selected day's opportunities. Matches the new mental model.
    const openAutomations = (automationsQ.data ?? []).filter(
      (a) => a.user_status === 'open' || a.user_status === 'activated',
    );
    const totalMinSaved = openAutomations.reduce((sum, a) => {
      const v = a.est_time_saved_per_week_minutes;
      return sum + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    }, 0);
    const tokens = selectedLog
      ? selectedLog.input_token_count + selectedLog.output_token_count
      : null;
    return [
      {
        label: 'Open automations',
        value: automationsQ.data ? openAutomations.length : null,
        secondary: 'in your queue',
        icon: Wrench,
        iconClassName: 'text-attention',
      },
      {
        label: 'Est. min/week saved',
        value: automationsQ.data ? totalMinSaved : null,
        secondary: 'sum across open queue',
        icon: Clock,
        iconClassName: 'text-forest',
      },
      {
        label: 'Tokens used',
        value: tokens,
        secondary: selectedLog
          ? `${selectedLog.input_token_count.toLocaleString()} in / ${selectedLog.output_token_count.toLocaleString()} out`
          : undefined,
        icon: Cpu,
        iconClassName: 'text-muted-foreground',
      },
    ];
  }, [automationsQ.data, selectedLog]);

  const historyLogs = useMemo(() => {
    if (!logsQ.data) return [];
    return logsQ.data.filter((l) => l.id !== effectiveLogId);
  }, [logsQ.data, effectiveLogId]);

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="space-y-3"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground flex items-center gap-3 flex-wrap">
              <Brain className="w-7 h-7 text-forest" aria-hidden />
              Intelligence
              <PulseDot color={todayHasLog ? 'green' : 'amber'} />
            </h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              The Brain reads the prior 7 days of Cognitive Memory and surfaces patterns — it never
              acts, you decide.
            </p>
            {mostRecent && (
              <p className="mt-1 text-xs text-muted-foreground/80">
                {todayHasLog ? (
                  <span className="inline-flex items-center gap-1.5 text-forest">
                    <PulseDot color="green" /> Today's digest is in.
                  </span>
                ) : (
                  <>Today's digest hasn't landed yet.</>
                )}{' '}
                Last run {formatDistanceToNow(new Date(mostRecent.window_end), { addSuffix: true })}
                .
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => regenerate.mutate()}
                disabled={regenerate.isPending || regenPending}
                aria-label="Regenerate today's digest"
              >
                {regenerate.isPending || regenPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                )}
                {regenPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
            )}
            {logsQ.data && logsQ.data.length > 0 && (
              <DigestDateDropdown
                logs={logsQ.data}
                selectedId={effectiveLogId}
                onChange={setSelectedLogId}
              />
            )}
          </div>
        </div>
      </motion.header>

      {logsQ.isLoading && <p className="text-sm text-muted-foreground">Loading digest history…</p>}
      {logsQ.isError && (
        <GlassCard hover={false} className="neo-alert">
          <p className="text-sm text-alert">
            Failed to load digest: {(logsQ.error as Error).message}
          </p>
        </GlassCard>
      )}

      {logsQ.data && logsQ.data.length === 0 && !logsQ.isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <GlassCard hover={false} variant="inset" className="text-center max-w-md">
            <Brain className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-foreground font-medium">Brain has not run yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              The daemon fires daily at 06:00 UTC. Once it runs you'll see a "where your time went"
              digest land here within a minute of completion.
            </p>
          </GlassCard>
        </div>
      )}

      {selectedLog && (
        <>
          <PageStatsRow stats={stats} />

          <SummaryCard log={selectedLog} />

          {entriesQ.isLoading && <p className="text-sm text-muted-foreground">Loading entries…</p>}

          {/* Automation Queue — persistent, owner-owned. Reads from the
              `automations` table (migration 0051), NOT digest_entries. Rows
              survive Brain regen + log soft-deletes; you have full control:
              Keep, Defer, Edit, Delete, Activate. */}
          <AutomationQueue
            rows={automationsQ.data ?? []}
            loading={automationsQ.isLoading}
            pendingIds={pendingAutomationIds}
            onDefer={(row) => setAutomationStatus.mutate({ row, user_status: 'deferred' })}
            onKeep={(row) => setAutomationStatus.mutate({ row, user_status: 'open' })}
            onDelete={(row) => setAutomationStatus.mutate({ row, user_status: 'deleted' })}
            onActivate={(row) => activateAutomation.mutate(row)}
            onEdit={(row) => setEditingRow(row)}
          />

          {editingRow && (
            <EditAutomationModal
              row={editingRow}
              open={!!editingRow}
              onOpenChange={(o) => {
                if (!o) setEditingRow(null);
              }}
            />
          )}

          {/* The descriptive kinds (time / relationships / patterns). */}
          {KIND_ORDER.filter((k) => k !== 'automation_opportunity').map((kind) => {
            const meta = KIND_META[kind];
            const rows = entriesByKind[kind];
            if (rows.length === 0) return null;
            return (
              <KindCard
                key={kind}
                kind={kind}
                meta={meta}
                rows={rows}
                relationshipSort={relationshipSort}
                onRelationshipSortChange={setRelationshipSort}
                onDismiss={(row) => dismissEntry.mutate({ row })}
              />
            );
          })}

          {/* Self-learning loop feed. */}
          <SkillsLearned />

          {entriesQ.data && entriesQ.data.length === 0 && !entriesQ.isLoading && (
            <GlassCard hover={false} variant="inset">
              <p className="text-sm text-muted-foreground">
                The Brain produced no entries for this digest. Either the week was quiet or Sonnet
                returned an empty-array digest.
              </p>
            </GlassCard>
          )}

          {historyLogs.length > 0 && (
            <PreviousDigestsDisclosure
              open={historyOpen}
              onToggle={() => setHistoryOpen((s) => !s)}
              logs={historyLogs}
              onSelect={(id) => {
                setSelectedLogId(id);
                setHistoryOpen(false);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

function DigestDateDropdown({
  logs,
  selectedId,
  onChange,
}: {
  logs: BrainLogRow[];
  selectedId: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="uppercase tracking-wide text-[10px]">Digest</span>
      <div className="relative">
        <select
          value={selectedId ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none neo-inset pl-3 pr-8 py-1.5 text-sm font-mono text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest/40"
          aria-label="Select digest date"
        >
          {logs.map((log) => (
            <option key={log.id} value={log.id}>
              {new Date(log.window_end).toISOString().slice(0, 10)}
            </option>
          ))}
        </select>
        <ChevronDown
          className="w-4 h-4 text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
          aria-hidden
        />
      </div>
    </label>
  );
}

function PreviousDigestsDisclosure({
  open,
  onToggle,
  logs,
  onSelect,
}: {
  open: boolean;
  onToggle: () => void;
  logs: BrainLogRow[];
  onSelect: (id: string) => void;
}) {
  return (
    <div className="pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
          aria-hidden
        />
        Previous digests ({logs.length})
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {logs.map((log) => (
            <li key={log.id}>
              <button
                type="button"
                onClick={() => onSelect(log.id)}
                className="w-full text-left neo-inset hover:bg-secondary/60 transition-colors px-3 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                  <span className="text-sm font-medium text-foreground font-mono">
                    {new Date(log.window_end).toISOString().slice(0, 10)}
                  </span>
                  <span className="ml-auto text-[11px] font-mono text-muted-foreground">
                    {formatDistanceToNow(new Date(log.window_end), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5">{log.summary}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SummaryCard({ log }: { log: BrainLogRow }) {
  const isShort = log.summary.trim().length < SHORT_SUMMARY_THRESHOLD;
  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-forest-soft text-forest">
          summary
        </span>
        <span className="text-[11px] font-mono text-muted-foreground ml-auto">
          window {new Date(log.window_start).toISOString().slice(0, 10)} →{' '}
          {new Date(log.window_end).toISOString().slice(0, 10)}
        </span>
      </div>
      {isShort ? (
        <div className="neo-inset bg-attention/5 p-3">
          <p className="text-xs text-attention font-medium">
            Summary unusually short — model may have skipped synthesis
          </p>
          <p className="text-sm text-muted-foreground mt-1 italic">{log.summary || '(empty)'}</p>
        </div>
      ) : (
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{log.summary}</p>
      )}
    </GlassCard>
  );
}

export type RelationshipSortKey = 'touchpoints' | 'recency';

export function KindCard({
  kind,
  meta,
  rows,
  relationshipSort,
  onRelationshipSortChange,
  onDismiss,
}: {
  kind: DigestEntryKind;
  meta: { label: string; icon: typeof Clock; tone: string };
  rows: DigestEntryRow[];
  relationshipSort: RelationshipSortKey;
  onRelationshipSortChange: (next: RelationshipSortKey) => void;
  // Optional so SSR tests + future read-only contexts can render the
  // card without wiring a mutation. When omitted the per-row X button
  // doesn't render — KindCard stays a pure presentational component.
  // Passes the full row (not just the id) so the parent mutation can
  // key its cache off row.source_brain_log_id and re-prepend on undo.
  onDismiss?: (row: DigestEntryRow) => void;
}) {
  const Icon = meta.icon;

  const sortedRows = useMemo(() => {
    if (kind !== 'top_relationship') return rows;
    const copy = [...rows];
    if (relationshipSort === 'touchpoints') {
      copy.sort((a, b) => {
        const ac = (a.payload as { touchpoint_count?: number }).touchpoint_count ?? 0;
        const bc = (b.payload as { touchpoint_count?: number }).touchpoint_count ?? 0;
        return bc - ac;
      });
    } else {
      copy.sort((a, b) => {
        const at = (a.payload as { last_touched_at?: string }).last_touched_at ?? '';
        const bt = (b.payload as { last_touched_at?: string }).last_touched_at ?? '';
        return bt.localeCompare(at);
      });
    }
    return copy;
  }, [kind, rows, relationshipSort]);

  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${meta.tone}`} aria-hidden />
        <h3 className="text-sm font-medium text-foreground">
          {meta.label} ({rows.length})
        </h3>
        {kind === 'top_relationship' && rows.length > 1 && (
          <div className="ml-auto flex items-center gap-1 text-[10px]">
            <Button
              size="sm"
              variant={relationshipSort === 'touchpoints' ? 'default' : 'ghost'}
              className="h-6 px-2 text-[10px]"
              onClick={() => onRelationshipSortChange('touchpoints')}
              aria-pressed={relationshipSort === 'touchpoints'}
            >
              by touches
            </Button>
            <Button
              size="sm"
              variant={relationshipSort === 'recency' ? 'default' : 'ghost'}
              className="h-6 px-2 text-[10px]"
              onClick={() => onRelationshipSortChange('recency')}
              aria-pressed={relationshipSort === 'recency'}
            >
              by recency
            </Button>
          </div>
        )}
      </div>
      <ul className="space-y-3">
        {sortedRows.map((row) => (
          <li key={row.id} className="neo-inset px-3 py-2.5">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <DigestEntryPayload kind={kind} payload={row.payload} />
              </div>
              {onDismiss && (
                <button
                  type="button"
                  onClick={() => onDismiss(row)}
                  className="shrink-0 p-1.5 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-secondary opacity-60 hover:opacity-100 transition-all focus:outline-none focus:ring-2 focus:ring-forest/40"
                  aria-label={`Dismiss this ${meta.label.toLowerCase()} entry`}
                  title="Dismiss — reappears only if the Brain re-surfaces it"
                >
                  <X className="w-3.5 h-3.5" aria-hidden />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

export function DigestEntryPayload({
  kind,
  payload,
}: {
  kind: DigestEntryKind;
  payload: Record<string, unknown>;
}) {
  if (kind === 'time_breakdown') {
    return <TimeBreakdownRow payload={payload} />;
  }
  if (kind === 'top_relationship') {
    return <TopRelationshipRow payload={payload} />;
  }
  if (kind === 'automation_opportunity') {
    return <AutomationOpportunityRow payload={payload} />;
  }
  return <PatternObservationRow payload={payload} />;
}

function TimeBreakdownRow({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as { bucket?: string; percent?: number; example_refs?: string[] };
  const percent = typeof p.percent === 'number' ? Math.max(0, Math.min(100, p.percent)) : null;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-sm font-medium text-foreground truncate">
          {p.bucket ?? '(unknown bucket)'}
        </span>
        <span className="text-sm font-mono text-forest shrink-0">
          {percent !== null ? `${Math.round(percent)}%` : '—'}
        </span>
      </div>
      <div className="h-2 rounded-full neo-inset bg-secondary overflow-hidden" aria-hidden>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: percent !== null ? `${percent}%` : 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
          className="h-full bg-forest rounded-full"
        />
      </div>
      {p.example_refs && p.example_refs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {p.example_refs.map((ref, i) => (
            <span
              key={i}
              className="neo-chip text-[10px] px-1.5 py-0.5 rounded-full bg-forest-soft/60 text-forest/90 font-mono"
            >
              {ref}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TopRelationshipRow({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as {
    counterparty?: string;
    touchpoint_count?: number;
    last_touched_at?: string;
    channels?: string[];
    note?: string;
  };
  const count = p.touchpoint_count ?? 0;
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground truncate">
          {p.counterparty ?? '(unknown)'}
        </span>
        {p.channels && p.channels.length > 0 && (
          <div className="flex items-center gap-1 shrink-0">
            {p.channels.map((c) => (
              <span
                key={c}
                className="neo-chip text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sage/15 text-sage"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        <span className="font-mono">{count}</span> touchpoint{count === 1 ? '' : 's'} in last 7 days
        {p.last_touched_at && (
          <>
            {' · last touched '}
            {formatDistanceToNow(new Date(p.last_touched_at), { addSuffix: true })}
          </>
        )}
      </p>
      {p.note && <p className="text-xs text-foreground mt-1.5 italic">{p.note}</p>}
    </div>
  );
}

function AutomationOpportunityRow({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as {
    opportunity?: string;
    evidence?: string;
    est_time_saved_per_week_minutes?: number;
    data_points?: number;
  };
  return (
    <div className="flex items-start gap-2">
      <Wrench className="w-4 h-4 text-attention shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{p.opportunity ?? '(none)'}</p>
        {p.evidence && (
          <blockquote className="mt-1.5 neo-inset p-3 text-xs text-muted-foreground italic">
            {p.evidence}
          </blockquote>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {typeof p.data_points === 'number' && (
            <span className="neo-chip inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-forest-soft text-forest font-mono">
              <Sparkles className="w-3 h-3" aria-hidden />
              {p.data_points} data points
            </span>
          )}
          {typeof p.est_time_saved_per_week_minutes === 'number' && (
            <span className="neo-chip inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-attention/15 text-attention font-mono">
              ~{p.est_time_saved_per_week_minutes} min/week saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PatternObservationRow({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as { observation?: string; confidence?: string; grounded_in?: string[] };
  const confidenceTone =
    p.confidence === 'high'
      ? 'bg-forest-soft text-forest'
      : p.confidence === 'medium'
        ? 'bg-learning/15 text-learning'
        : 'bg-secondary text-muted-foreground';
  return (
    <div className="flex items-start gap-2">
      <Sparkles className="w-4 h-4 text-learning shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{p.observation ?? '(none)'}</p>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {p.confidence && (
            <span
              className={`neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${confidenceTone}`}
            >
              {p.confidence} confidence
            </span>
          )}
          {p.grounded_in &&
            p.grounded_in.map((g, i) => (
              <span
                key={i}
                className="neo-chip text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-mono"
              >
                {g}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Automation Queue — persistent, owner-owned. Reads AutomationRow directly
// from the `automations` table (migration 0051). Rows survive Brain regen +
// log soft-deletes; owner has full control (Keep, Defer, Edit, Delete,
// Activate). The 4→2→1 melt from the digest-scoped queue is architecturally
// gone: the query key ['automations', ownerId] never changes with brain-log
// selection.
// ===========================================================================

function AutomationCard({
  row,
  onDefer,
  onKeep,
  onEdit,
  onDelete,
  onActivate,
  isPending,
}: {
  row: AutomationRow;
  onDefer: (row: AutomationRow) => void;
  onKeep: (row: AutomationRow) => void;
  onEdit: (row: AutomationRow) => void;
  onDelete: (row: AutomationRow) => void;
  onActivate: (row: AutomationRow) => void;
  isPending: boolean;
}) {
  const activated = row.user_status === 'activated';
  const deferred = row.user_status === 'deferred';
  const dp = row.data_points ?? 0;
  const target = Math.max(row.data_points_target ?? dp, 1);
  const confidence = row.confidence ?? Math.round((dp / target) * 100);
  const isAgent = row.agent_type !== 'skill';
  const ready = !activated && (row.status === 'ready' || dp >= target);
  const canActivate = !activated && (ready || confidence >= 80);
  const pct = Math.min(100, Math.round((dp / target) * 100));

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="h-full">
      <GlassCard
        hover={false}
        className={`h-full ${ready ? 'neo-pulse-glow' : ''} ${deferred ? 'opacity-70' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="neo-chip inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-forest-soft text-forest font-medium">
              {isAgent ? <Bot className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {isAgent ? 'New Agent' : 'New Skill'}
            </span>
            {activated ? (
              <span className="neo-chip text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage font-medium">
                ✓ Activated
              </span>
            ) : deferred ? (
              <span className="neo-chip text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">
                Deferred
              </span>
            ) : ready ? (
              <span className="neo-chip text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage font-medium animate-pulse">
                Ready
              </span>
            ) : null}
          </div>
          <span className="text-xs font-mono text-muted-foreground shrink-0">{confidence}%</span>
        </div>

        <p className="text-sm font-medium text-foreground leading-snug mt-2.5">{row.opportunity}</p>
        {row.evidence && (
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{row.evidence}</p>
        )}

        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>Confidence</span>
            <span className="font-mono">
              {dp}/{target} data points
            </span>
          </div>
          <div className="h-1.5 rounded-full neo-inset bg-secondary overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${ready ? 'bg-sage' : 'bg-forest'}`}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {activated ? (
            <span className="neo-chip text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage font-medium">
              Activated
            </span>
          ) : (
            <Button
              size="sm"
              variant={ready ? 'default' : 'outline'}
              disabled={!canActivate || isPending}
              onClick={() => onActivate(row)}
              aria-label={`Activate: ${row.opportunity}`}
            >
              <Play className="w-3 h-3 mr-1" />
              Activate
            </Button>
          )}
          {!activated && !deferred && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDefer(row)}
              disabled={isPending}
              aria-label={`Defer: ${row.opportunity}`}
            >
              <Pause className="w-3 h-3 mr-1" />
              Defer
            </Button>
          )}
          {deferred && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onKeep(row)}
              disabled={isPending}
              aria-label={`Keep: ${row.opportunity}`}
            >
              <Play className="w-3 h-3 mr-1" />
              Keep
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(row)}
            disabled={isPending}
            aria-label={`Edit: ${row.opportunity}`}
          >
            <Pencil className="w-3 h-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(row)}
            disabled={isPending}
            aria-label={`Delete: ${row.opportunity}`}
            className="text-muted-foreground hover:text-alert"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Delete
          </Button>
          {typeof row.est_time_saved_per_week_minutes === 'number' && (
            <span className="ml-auto text-[10px] text-attention font-mono">
              ~{row.est_time_saved_per_week_minutes} min/wk
            </span>
          )}
        </div>
      </GlassCard>
    </motion.div>
  );
}

function AutomationQueue({
  rows,
  loading,
  pendingIds,
  onDefer,
  onKeep,
  onEdit,
  onDelete,
  onActivate,
}: {
  rows: AutomationRow[];
  loading: boolean;
  pendingIds: Set<string>;
  onDefer: (row: AutomationRow) => void;
  onKeep: (row: AutomationRow) => void;
  onEdit: (row: AutomationRow) => void;
  onDelete: (row: AutomationRow) => void;
  onActivate: (row: AutomationRow) => void;
}) {
  const open = rows.filter((r) => r.user_status === 'open' || r.user_status === 'activated');
  const deferred = rows.filter((r) => r.user_status === 'deferred');
  const [showDeferred, setShowDeferred] = useState(false);

  return (
    <section>
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
        <Zap className="w-4 h-4 text-attention" />
        Automation Queue
        <span className="text-muted-foreground font-normal">({open.length})</span>
      </h2>
      <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
        Your persistent queue. New proposals from the Brain stick here; edits and Keep/Defer/Delete
        are yours. Rows survive digests and Regenerate.
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading queue…</p>
      ) : open.length === 0 ? (
        <GlassCard hover={false} variant="inset">
          <p className="text-sm text-muted-foreground">
            No open automations. The Brain proposes new patterns each day; open ones stick here
            until you Activate, Defer, or Delete them.
          </p>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {open.map((row) => (
            <AutomationCard
              key={row.id}
              row={row}
              onDefer={onDefer}
              onKeep={onKeep}
              onEdit={onEdit}
              onDelete={onDelete}
              onActivate={onActivate}
              isPending={pendingIds.has(row.id)}
            />
          ))}
        </div>
      )}

      {deferred.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDeferred((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-expanded={showDeferred}
          >
            {showDeferred ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            Deferred ({deferred.length})
          </button>
          {showDeferred && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-3">
              {deferred.map((row) => (
                <AutomationCard
                  key={row.id}
                  row={row}
                  onDefer={onDefer}
                  onKeep={onKeep}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onActivate={onActivate}
                  isPending={pendingIds.has(row.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// Skills Learned — the self-learning loop feed. Each entry is a discrete skill
// the orchestrator / a sub-agent got better at as real data accumulated.
// ===========================================================================

type AgentLearningRow = {
  id: string;
  agent: string;
  title: string;
  source: string | null;
  learned_at: string;
};

const AGENT_META: Record<string, { label: string; color: string }> = {
  orchestrator: { label: 'Orchestrator', color: '#1F6F54' },
  email: { label: 'Email Agent', color: '#2D7D5F' },
  meeting: { label: 'Meeting Agent', color: '#4B7A6A' },
  agreement: { label: 'Agreement Agent', color: '#7A5C2E' },
  commission: { label: 'Commission Agent', color: '#9A6A1B' },
  focus: { label: 'Focus Guardian', color: '#3B6E8F' },
  messages: { label: 'Messages Agent', color: '#6A4B7A' },
  learning_loop: { label: 'Learning Loop', color: '#1F6F54' },
};

async function fetchLearnings(): Promise<AgentLearningRow[]> {
  const { data, error } = await supabase
    .from('agent_learnings')
    .select('id, agent, title, source, learned_at')
    .order('learned_at', { ascending: false })
    .limit(12);
  if (error) return []; // table may not exist on an older deploy — degrade quietly
  return (data as unknown as AgentLearningRow[]) ?? [];
}

function SkillsLearned() {
  const q = useQuery({ queryKey: ['agent_learnings'], queryFn: fetchLearnings, staleTime: 60_000 });
  const rows = q.data ?? [];
  if (q.isLoading || rows.length === 0) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
        <GraduationCap className="w-4 h-4 text-forest" />
        Skills Learned
        <span className="text-muted-foreground font-normal">({rows.length})</span>
      </h2>
      <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
        The orchestrator and its sub-agents get sharper with every interaction — these are the
        skills they&apos;ve picked up from your captured data.
      </p>
      <GlassCard hover={false}>
        <div className="relative">
          <div
            className="absolute left-1 top-1 bottom-1 w-px"
            style={{ background: 'var(--border-soft)' }}
          />
          {rows.map((s) => {
            const meta = AGENT_META[s.agent] ?? { label: s.agent, color: '#5B6172' };
            return (
              <div key={s.id} className="relative flex items-start gap-3 py-2.5 pl-5">
                <span
                  className="absolute left-0 top-3.5 w-2.5 h-2.5 rounded-full ring-2 ring-card"
                  style={{ background: meta.color }}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium"
                      style={{ background: `${meta.color}1a`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {formatDistanceToNow(new Date(s.learned_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-foreground mt-1 leading-snug">{s.title}</p>
                  {s.source && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.source}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </GlassCard>
    </section>
  );
}

export default Intel;
